import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { KeyRound } from 'lucide-react';
import { LEGAL_VERSIONS } from '@/constants/legal';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  // Ref to prevent duplicate verification attempts (React strict mode / double renders)
  const verificationAttempted = useRef(false);

  const token = searchParams.get('token');

  useEffect(() => {
    // Prevent duplicate verification attempts
    if (verificationAttempted.current) {
      return;
    }
    verificationAttempted.current = true;

    const verifyToken = async () => {
      if (!token) {
        setError('Invalid or missing invitation token');
        setIsVerifying(false);
        return;
      }

      try {
        // CRITICAL: Sign out any existing session first to prevent session contamination
        // This ensures the invite token establishes the CORRECT user's session
        await supabase.auth.signOut({ scope: 'local' });
        
        // Brief delay to ensure localStorage is cleared
        await new Promise(resolve => setTimeout(resolve, 100));

        // Attempt to verify the OTP token
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery',
        });

        if (verifyError) {
          // On error, wait briefly and re-check session
          // (Supabase sometimes establishes session even on 400 response)
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const { data: retrySession } = await supabase.auth.getSession();
          
          if (retrySession?.session?.user) {
            setTokenVerified(true);
            setIsVerifying(false);
            return;
          }
          
          setError('This invitation link has expired or is invalid. Please request a new invitation.');
        } else {
          setTokenVerified(true);
        }
      } catch (err) {
        // Even on exception, check if session exists
        const { data: fallbackSession } = await supabase.auth.getSession();
        if (fallbackSession?.session?.user) {
          setTokenVerified(true);
        } else {
          setError('Failed to verify invitation. Please try again or request a new invitation.');
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    if (!termsAccepted) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'You must accept the Terms of Service and Privacy Policy.',
      });
      setIsLoading(false);
      return;
    }

    const formData = new FormData(e.currentTarget);
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    if (password !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Passwords do not match',
      });
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Password must be at least 6 characters',
      });
      setIsLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: updateError.message,
        });
      } else {
        // Record legal consent — best-effort
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (userId) {
          const now = new Date().toISOString();
          supabase.from('legal_consents').insert([
            { user_id: userId, document_type: 'tos', document_version: LEGAL_VERSIONS.tos, accepted_at: now },
            { user_id: userId, document_type: 'privacy_policy', document_version: LEGAL_VERSIONS.privacy_policy, accepted_at: now },
          ]).then(({ error: consentError }) => {
            if (consentError) console.warn('[Legal] Failed to record consent:', consentError.message);
          });
        }

        toast({
          title: 'Success',
          description: 'Your password has been set. Completing setup...',
        });
        setTimeout(() => navigate('/onboarding'), 1000);
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to set password. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-destructive">Invitation Error</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate('/auth')} className="w-full">
              Go to Sign In
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            Create Your Password
          </CardTitle>
          <CardDescription>
            Set a secure password for your account
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSetPassword}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                minLength={6}
                placeholder="Enter a secure password"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                minLength={6}
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
            </div>
            <div className="flex items-start space-x-2 pt-2">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                className="mt-0.5"
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground leading-snug cursor-pointer">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-primary underline hover:text-primary/80">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="text-primary underline hover:text-primary/80">
                  Privacy Policy
                </Link>
              </label>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={isLoading || !termsAccepted}>
              {isLoading ? 'Setting Password...' : 'Set Password & Continue'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default AcceptInvite;
